import { useUser } from '../context/UserContext';

export interface PermissionCheckResult {
  hasAccess: boolean;
  reason?: string;
}

export const usePermissions = () => {
  const { user } = useUser();

  const isAdmin = () => user?.rol === 'ADMIN';
  const isAsociado = () => user?.rol === 'ASOCIADO';
  const isAfiliado = () => user?.rol === 'AFILIADO';
  const isPublic = () => user?.rol === 'PUBLICO' || !user;

  // Verificar si el usuario puede gestionar una propiedad específica
  const canManageProperty = (propertyData?: any): PermissionCheckResult => {
    if (!user) return { hasAccess: false, reason: 'No authenticated' };
    
    if (isAdmin()) return { hasAccess: true };
    
    if (isAsociado() && propertyData?.inmobiliaria_id) {
      const hasAccess = user.inmobiliaria_id === propertyData.inmobiliaria_id;
      return { 
        hasAccess, 
        reason: hasAccess ? undefined : 'Property belongs to different agency' 
      };
    }
    
    if (isAfiliado() && propertyData?.id) {
      const hasAccess = user.propiedad_id?.includes(propertyData.id) || false;
      return { 
        hasAccess, 
        reason: hasAccess ? undefined : 'Property not assigned to this affiliate' 
      };
    }
    
    return { hasAccess: false, reason: 'Insufficient permissions' };
  };

  // Verificar si el usuario puede gestionar una inmobiliaria específica
  const canManageAgency = (agencyId?: string): PermissionCheckResult => {
    if (!user) return { hasAccess: false, reason: 'No authenticated' };
    
    if (isAdmin()) return { hasAccess: true };
    
    if (isAsociado() && agencyId) {
      const hasAccess = user.inmobiliaria_id === agencyId;
      return { 
        hasAccess, 
        reason: hasAccess ? undefined : 'Can only manage own agency' 
      };
    }
    
    return { hasAccess: false, reason: 'Only admin and associated users can manage agencies' };
  };

  // Verificar si el usuario puede gestionar un proyecto específico
  const canManageProject = (projectData?: any): PermissionCheckResult => {
    if (!user) return { hasAccess: false, reason: 'No authenticated' };
    
    if (isAdmin()) return { hasAccess: true };
    
    if (isAsociado() && projectData?.inmobiliaria_id) {
      const hasAccess = user.inmobiliaria_id === projectData.inmobiliaria_id;
      return { 
        hasAccess, 
        reason: hasAccess ? undefined : 'Project belongs to different agency' 
      };
    }
    
    return { hasAccess: false, reason: 'Only admin and associated users can manage projects' };
  };

  // Verificar permisos por categoría de acción
  const checkPermission = (action: string, resourceData?: any): PermissionCheckResult => {
    if (!user) return { hasAccess: false, reason: 'No authenticated' };

    const permissions: Record<string, () => PermissionCheckResult> = {
      // Solo ADMIN
      'manage-all-properties': () => ({ hasAccess: isAdmin() }),
      'manage-all-agencies': () => ({ hasAccess: isAdmin() }),
      'manage-all-projects': () => ({ hasAccess: isAdmin() }),
      'manage-all-affiliates': () => ({ hasAccess: isAdmin() }),
      'manage-all-associates': () => ({ hasAccess: isAdmin() }),
      'approve-properties': () => ({ hasAccess: isAdmin() }),
      'approve-reviews': () => ({ hasAccess: isAdmin() }),
      'manage-all-courses': () => ({ hasAccess: isAdmin() }),
      'create-course': () => ({ hasAccess: isAdmin() }),
      'edit-course': () => ({ hasAccess: isAdmin() }),
      'manage-attendance': () => ({ hasAccess: isAdmin() }),
      'scan-attendance': () => ({ hasAccess: isAdmin() }),
      'create-associate': () => ({ hasAccess: isAdmin() }),
      'manage-assignments': () => ({ hasAccess: isAdmin() }),

      // ADMIN + ASOCIADO
      'create-affiliate': () => ({ hasAccess: isAdmin() || isAsociado() }),
      'create-property': () => ({ hasAccess: isAdmin() || isAsociado() }),
      'edit-agency': () => canManageAgency(resourceData?.id || resourceData?.inmobiliaria_id),

      // ADMIN + ASOCIADO + AFILIADO
      'manage-property': () => canManageProperty(resourceData),
      'edit-property': () => canManageProperty(resourceData),
      'access-dashboard': () => ({ hasAccess: !isPublic() }),

      // ASOCIADO + AFILIADO
      'edit-profile': () => ({ hasAccess: isAsociado() || isAfiliado() }),
      'view-my-properties': () => ({ hasAccess: isAsociado() || isAfiliado() }),
      'view-my-projects': () => ({ hasAccess: isAsociado() || isAfiliado() }),
      'view-project': () => canManageProject(resourceData),

      // Solo ASOCIADO
      'edit-projects': () => canManageProject(resourceData),

      // Públicas (siempre permitidas)
      'view-agencies': () => ({ hasAccess: true }),
      'view-agency': () => ({ hasAccess: true }),
      'view-affiliates': () => ({ hasAccess: true }),
      'view-affiliate': () => ({ hasAccess: true }),
      'view-associates': () => ({ hasAccess: true }),
      'view-associate': () => ({ hasAccess: true }),
      'view-properties': () => ({ hasAccess: true }),
      'view-property': () => ({ hasAccess: true }),
      'add-review': () => ({ hasAccess: true }),
      'view-courses': () => ({ hasAccess: true }),
      'enroll-course': () => ({ hasAccess: true }),
      'view-course': () => ({ hasAccess: true })
    };

    const permissionCheck = permissions[action];
    if (!permissionCheck) {
      return { hasAccess: false, reason: `Unknown action: ${action}` };
    }

    return permissionCheck();
  };

  return {
    user,
    isAdmin,
    isAsociado,
    isAfiliado,
    isPublic,
    canManageProperty,
    canManageAgency,
    canManageProject,
    checkPermission
  };
};

/**
 * Hook para verificar permisos específicos de páginas según las reglas establecidas
 */
export const usePagePermissions = () => {
  const { isAdmin, isAsociado, isAfiliado, canManageProperty, canManageAgency, canManageProject } = usePermissions();

  const pagePermissions = {
    // Solo ADMIN
    adminDashboard: () => isAdmin(),
    manageAllProperties: () => isAdmin(),
    manageAllAgencies: () => isAdmin(), 
    manageAllProjects: () => isAdmin(),
    manageAllAffiliates: () => isAdmin(),
    manageAllAssociates: () => isAdmin(),
    approveProperties: () => isAdmin(),
    approveReviews: () => isAdmin(),
    manageAllCourses: () => isAdmin(),
    createCourse: () => isAdmin(),
    editCourse: () => isAdmin(),
    manageAttendance: () => isAdmin(),
    scanAttendance: () => isAdmin(),
    createAssociate: () => isAdmin(),
    
    // ADMIN + ASOCIADO
    createAffiliate: () => isAdmin() || isAsociado(),
    createProperty: () => isAdmin() || isAsociado(),
    editAgency: (agencyId?: string) => canManageAgency(agencyId).hasAccess,
    
    // ADMIN + ASOCIADO + AFILIADO
    manageDashboard: () => isAdmin() || isAsociado() || isAfiliado(),
    editProperty: (propertyData?: any) => canManageProperty(propertyData).hasAccess,
    
    // ASOCIADO + AFILIADO  
    editProfile: () => isAsociado() || isAfiliado(),
    viewMyProperties: () => isAsociado() || isAfiliado(),
    viewMyProjects: () => isAsociado() || isAfiliado(),
    viewProject: (projectData?: any) => canManageProject(projectData).hasAccess,
    
    // Solo ASOCIADO
    editProjects: (projectData?: any) => isAsociado() && canManageProject(projectData).hasAccess,
    
    // Siempre públicas
    viewAgencies: () => true,
    viewAgency: () => true,
    viewAffiliates: () => true,
    viewAffiliate: () => true,
    viewAssociates: () => true,
    viewAssociate: () => true,
    viewProperties: () => true,
    viewProperty: () => true,
    addReview: () => true,
    viewCourses: () => true,
    enrollCourse: () => true,
    viewCourse: () => true
  };

  return pagePermissions;
};